// FILE: components/rewards/RewardModal.tsx
// Base UI cho mọi loại reward popup. Config-driven qua preset.

"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { X } from "lucide-react";
import JIBI from "@/components/mascot/JIBI";
import { markWelcomeShown } from "@/actions/rewards";
import type { RewardPreset } from "./rewardPresets";

interface Props {
  preset: RewardPreset;
  /** Giá trị động insert vào template body/amount */
  amount?: number;
  /** Callback sau khi modal đóng (dùng để clear queue) */
  onClose: () => void;
}

const ACCENT_MAP = {
  gold: {
    glow: "from-amber-300/40 via-yellow-200/30 to-transparent",
    amountBg: "from-amber-400 via-yellow-500 to-orange-500",
    cta: "from-orange-500 to-amber-500 hover:shadow-[0_8px_30px_rgba(249,115,22,.4)]",
    confetti: ["#fbbf24", "#f59e0b", "#fde047", "#fb923c"],
  },
  purple: {
    glow: "from-purple-300/40 via-fuchsia-200/30 to-transparent",
    amountBg: "from-purple-500 via-fuchsia-500 to-pink-500",
    cta: "from-purple-600 to-fuchsia-600 hover:shadow-[0_8px_30px_rgba(168,85,247,.4)]",
    confetti: ["#a855f7", "#c084fc", "#e879f9", "#f0abfc"],
  },
  teal: {
    glow: "from-teal-300/40 via-cyan-200/30 to-transparent",
    amountBg: "from-teal-500 via-cyan-500 to-emerald-500",
    cta: "from-teal-600 to-emerald-600 hover:shadow-[0_8px_30px_rgba(20,184,166,.4)]",
    confetti: ["#14b8a6", "#06b6d4", "#10b981", "#34d399"],
  },
};

export default function RewardModal({ preset, amount, onClose }: Props) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(true);
  const firedRef = useRef(false);

  const accent = ACCENT_MAP[preset.accent];

  // Confetti burst khi mở
  useEffect(() => {
    if (!preset.confetti || firedRef.current) return;
    firedRef.current = true;

    let cancelled = false;
    import("canvas-confetti").then(({ default: confetti }) => {
      if (cancelled) return;

      const fire = (particleRatio: number, opts: Parameters<typeof confetti>[0]) => {
        confetti({
          origin: { y: 0.35 },
          colors: accent.confetti,
          particleCount: Math.floor(200 * particleRatio),
          ...opts,
        });
      };

      fire(0.25, { spread: 26, startVelocity: 55 });
      fire(0.2, { spread: 60 });
      fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fire(0.1, { spread: 120, startVelocity: 45 });
    });

    return () => { cancelled = true; };
  }, [preset.confetti, accent.confetti]);

  // Body text - replace {amount}
  const bodyText = preset.body.replace("{amount}", String(amount ?? 0));

  function handleCta() {
    startTransition(async () => {
      // Persist dismiss state - hiện tại chỉ welcome_ref dùng column này.
      // Future: thêm branching theo preset.type khi có preset khác cần persist.
      if (preset.type === "welcome_ref" || preset.type === "welcome_plain") {
        await markWelcomeShown();
      }
      setOpen(false);
      // Delay để animation fade out xong mới unmount
      setTimeout(() => {
        onClose();
        if (preset.ctaHref) router.push(preset.ctaHref);
      }, 200);
    });
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-visible">
        {/* Hào quang gradient center */}
        <div className={`pointer-events-none absolute inset-0 rounded-3xl bg-gradient-radial ${accent.glow}`} />

        {/* Close button nhỏ góc trên */}
        <button
          onClick={handleCta}
          disabled={isPending}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-muted/60 flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors z-20 disabled:opacity-50"
          aria-label="Đóng"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Mascot ló từ trên — JIBI base, pose waving (KHÔNG tạo variant mới) */}
        <div className="relative flex justify-center -mt-20 z-10 pointer-events-none">
          <JIBI pose="waving" size="lg" animate />
        </div>

        {/* Content */}
        <div className="relative px-7 pt-4 pb-7 text-center">
          {/* Amount block */}
          {preset.showAmount && typeof amount === "number" && (
            <div className="flex justify-center mb-4">
              <span
                className={`inline-flex items-baseline gap-1 text-[44px] font-extrabold leading-none bg-gradient-to-br ${accent.amountBg} bg-clip-text text-transparent animate-bounce-slow drop-shadow-sm`}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                +{amount}
                <span className="text-[32px]">💎</span>
              </span>
            </div>
          )}

          <h2 className="text-[22px] font-extrabold text-foreground mb-1">{preset.title}</h2>
          <p className="text-[13px] text-muted-foreground mb-4">{preset.subtitle}</p>

          <p className="text-[13px] text-foreground/80 leading-relaxed mb-6 px-2">
            {bodyText.split(/(\{amount\}|\d+ Kim cương)/).map((part, i) =>
              /\d+ Kim cương/.test(part) ? (
                <strong key={i} className="text-orange-600 font-bold">{part}</strong>
              ) : (
                part
              )
            )}
          </p>

          <button
            onClick={handleCta}
            disabled={isPending}
            className={`w-full py-3.5 rounded-full bg-gradient-to-r ${accent.cta} text-white text-[14px] font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60`}
          >
            {preset.ctaLabel}
          </button>
        </div>

        <style jsx>{`
          .animate-fade-in { animation: fadeIn 0.3s ease-out; }
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          .animate-bounce-slow { animation: bounceSlow 2s ease-in-out infinite; }
          @keyframes bounceSlow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }
          .bg-gradient-radial {
            background-image: radial-gradient(circle at center, var(--tw-gradient-stops));
          }
        `}</style>
      </div>
    </div>
  );
}